<template>
  <div class="admin-team-container">
    <h2>Manage Team Members</h2>
    <button @click="logout" class="logout-btn">Logout</button>
    <div v-if="error" class="error">{{ error }}</div>
    <table>
      <thead>
        <tr>
          <th>Image</th>
          <th>Name</th>
          <th>Designation</th>
          <th>Testimonial</th>
          <th>Socials</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="member in team" :key="member.id">
          <td>
            <img :src="`http://localhost:5001/api/team/${member.id}/image`" alt="img" width="60" />
            <input type="file" @change="e => uploadImage(e, member)" />
          </td>
          <td><input v-model="member.name" /></td>
          <td><input v-model="member.designation" /></td>
          <td><textarea v-model="member.testimonial" /></td>
          <td>
            <div v-for="(social, sidx) in member.socials" :key="sidx" class="social-row">
              <select v-model="social.icon">
                <option v-for="icon in socialIcons" :value="icon.path" :key="icon.path">{{ icon.label }}</option>
              </select>
              <input v-model="social.url" placeholder="URL" />
              <input v-model="social.alt" placeholder="Alt" />
              <button @click="removeSocial(member, sidx)">Remove</button>
            </div>
            <button @click="addSocial(member)">Add Social</button>
          </td>
          <td>
            <button @click="updateMember(member)">Save</button>
            <button @click="deleteMember(member.id)">Delete</button>
          </td>
        </tr>
        <tr>
          <td>
            <img v-if="newMember.image_blob_preview" :src="newMember.image_blob_preview" alt="img" width="60" />
            <input type="file" @change="e => uploadImage(e, newMember)" />
          </td>
          <td><input v-model="newMember.name" placeholder="Name" /></td>
          <td><input v-model="newMember.designation" placeholder="Designation" /></td>
          <td><textarea v-model="newMember.testimonial" placeholder="Testimonial" /></td>
          <td>
            <div v-for="(social, sidx) in newMember.socials" :key="sidx" class="social-row">
              <select v-model="social.icon">
                <option v-for="icon in socialIcons" :value="icon.path" :key="icon.path">{{ icon.label }}</option>
              </select>
              <input v-model="social.url" placeholder="URL" />
              <input v-model="social.alt" placeholder="Alt" />
              <button @click="removeSocial(newMember, sidx)">Remove</button>
            </div>
            <button @click="addSocial(newMember)">Add Social</button>
          </td>
          <td><button @click="addMember">Add</button></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRouter } from "vue-router";

const socialIcons = [
  { label: 'WhatsApp', path: '/images/whatapp.png', alt: 'icon-whatsapp' },
  { label: 'Facebook', path: '/images/icon-facebook.png', alt: 'icon-facebook' },
  { label: 'Instagram', path: '/images/icon-instagram.png', alt: 'icon-instagram' },
];

export default defineComponent({
  name: "AdminTeam",
  setup() {
    const team = ref([]);
    const newMember = ref({ image: '', name: '', designation: '', testimonial: '', socials: [], image_blob: '', image_blob_preview: '' });
    const error = ref('');
    const router = useRouter();

    const getToken = () => localStorage.getItem('admin_token');

    const fetchTeam = async () => {
      const res = await fetch("http://localhost:5001/api/team");
      const data = await res.json();
      // Ensure socials is always an array
      team.value = data.map((m: any) => ({ ...m, socials: m.socials || [] }));
    };

    const uploadImage = async (e: any, member: any) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = (event.target?.result as string).split(',')[1];
        member.image_blob = base64;
        // For preview
        member.image_blob_preview = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    };

    const addMember = async () => {
      error.value = '';
      const { image_blob_preview, ...payload } = newMember.value;
      const res = await fetch("http://localhost:5001/api/team", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await fetchTeam();
        newMember.value = { image: '', name: '', designation: '', testimonial: '', socials: [], image_blob: '', image_blob_preview: '' };
      } else {
        error.value = 'Failed to add member (check login)';
      }
    };

    const updateMember = async (member: any) => {
      error.value = '';
      const res = await fetch(`http://localhost:5001/api/team/${member.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(member),
      });
      if (!res.ok) error.value = 'Failed to update member (check login)';
    };

    const deleteMember = async (id: number) => {
      error.value = '';
      const res = await fetch(`http://localhost:5001/api/team/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (res.ok) {
        await fetchTeam();
      } else {
        error.value = 'Failed to delete member (check login)';
      }
    };

    const addSocial = (member: any) => {
      if (!member.socials) member.socials = [];
      member.socials.push({ icon: socialIcons[0].path, url: '', alt: socialIcons[0].alt });
    };
    const removeSocial = (member: any, idx: number) => {
      if (member.socials) member.socials.splice(idx, 1);
    };

    const logout = () => {
      localStorage.removeItem('admin_token');
      router.push('/admin/login');
    };

    onMounted(() => {
      if (!getToken()) router.push('/admin/login');
      fetchTeam();
    });

    return { team, newMember, addMember, updateMember, deleteMember, error, logout, uploadImage, addSocial, removeSocial, socialIcons };
  },
});
</script>

<style scoped>
.admin-team-container {
  max-width: 1000px;
  margin: 40px auto;
  background: #fff;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.04);
}
.admin-team-container h2 {
  text-align: center;
  margin-bottom: 24px;
}
.admin-team-container table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 24px;
}
.admin-team-container th, .admin-team-container td {
  border: 1px solid #eee;
  padding: 8px;
  text-align: left;
  color: Black;
}
.admin-team-container input, .admin-team-container textarea, .admin-team-container select {
  width: 100%;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.admin-team-container button {
  margin: 2px;
  padding: 6px 12px;
  background: #4c4cff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.logout-btn {
  float: right;
  background: #d00;
}
.error {
  color: #d00;
  margin-top: 10px;
  text-align: center;
}
.social-row {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  align-items: center;
}
.social-row select, .social-row input {
  flex: 1;
}
.social-row button {
  background: #d00;
  padding: 4px 8px;
}
</style> 